import React from 'react';
import Button from "components/Button";
import {ExclamationCircleIcon, PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Field from "components/Form/Field";
import FieldHeader from "components/Form/FieldHeader";
import Well from "components/Well";
import {FieldArrayRenderProps} from "formik";
import {Feature} from "geojson";
import {MapEditor} from "../../../components/MapEditor";
import {featureCollection} from "../../../services/geometry";
import {FloorData} from "../../../types/Floor";
import Map from "../../../components/Map";
import {useOperator} from "../../../context/OperatorContext";
import {getViewState} from "../../../services/map";
import {Building} from "../../../types/Building";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";

// Types ----

type FloorFormFieldsProps = {
  building?: Building | null
  floor: FloorData
  index: number
  helpers: FieldArrayRenderProps
  name?: string
  readOnly?: boolean
}

// Component ----

export default function FloorFormFields({building, floor, index, helpers, name = 'floors', readOnly = false}: FloorFormFieldsProps) {
  const {settings, termFor} = useOperator();

  const currentBuilding = featureCollection(building?.footprint ? [
    {type: "Feature", properties: {type: 'building'}, geometry: building?.footprint}
  ] : []);

  const currentFloor = featureCollection(floor.area? [
    {type: "Feature", properties: {type: 'floor'}, geometry: floor.area}
  ] : []);

  function handleFeaturesChanged(features: Array<Feature>) {
    const area = features.find(feature => feature.geometry.type === 'Polygon')?.geometry ?? null;

    void helpers.form.setFieldValue(`${name}.${index}.area`, area);
  }

  return (
    <Well
      heading={floor.name || (floor.order ? `${termFor('Floor')} ${floor.order}` : `New ${termFor('floor')}`)}
      action={
        <Button
          color={Button.colors.WHITE}
          size={Button.sizes.X_SMALL}
          icon={TrashIcon}
          onClick={() => helpers.remove(index)}
          disabled={readOnly}
        />
      }>

      <div className="grid grid-cols-1 gap-y-6 gap-x-4 lg:grid-cols-6">

        {/* Name */}
        <div className="sm:col-span-6">
          <Field
            label="Name *"
            type="text"
            name={`${name}.${index}.name`}
            maxLength={255}
            disabled={readOnly}
          />
        </div>

        {/* Order */}
        <div className="sm:col-span-2">
          <Field
            label="Order *"
            type="number"
            name={`${name}.${index}.order`}
            disabled={readOnly}
          />
        </div>

        {/* Height */}
        <div className="sm:col-span-3">
          <Field
            label="Height (in metres from ground level) *"
            type="number"
            name={`${name}.${index}.height`}
            disabled={readOnly}
          />
        </div>

        {/* Information */}
        <div className="sm:col-span-6">
          <FieldHeader label="Area *"/>
          <div className="mt-1">

            {readOnly ? (
                <Map
                  height='32rem'
                  defaultMapStyle={settings?.audience_app.map_style ?? null}
                  initialViewState={getViewState(currentBuilding, settings?.audience_app.map_state)}
                  buildingId={building?.id}
                  floorId={floor.id}
                />
              ) : (
                <>
                  <MapEditor
                    height="32rem"
                    tools={{polygon: true}}
                    features={currentFloor}
                    onFeaturesChanged={handleFeaturesChanged}
                    singleFeature
                    initialViewState={!currentFloor.features.length ? getViewState(currentBuilding, settings?.audience_app.map_state) : null}
                  />

                  <p className="mt-2 text-sm text-gray-500 flex items-center">
                    <PencilIcon className="text-gray-600 h-4 w-4 mr-1"/> Draw the {termFor('floor')} area on the map using the drawing tool
                  </p>

                  {/* @ts-ignore */}
                  {(!!helpers.form.errors.floors?.[index]?.area.coordinates && helpers.form.touched.floors?.[index]?.area) &&
                    <FieldHelp isError>
                      <div className="flex items-center gap-2">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true"/>
                        {/* @ts-ignore */}
                        {helpers.form.errors.floors[index].area.coordinates}
                      </div>
                    </FieldHelp>
                  }
                </>
             )}
          </div>
        </div>
      </div>
    </Well>
  );
}